import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ReactNode } from "react";

interface SubmitClaimSectionProps {
  index: number;
  title: string;
  description: string;
  content: ReactNode;
}

/**
 * A numbered section of the submit-claim form: a brand step circle on the left,
 * then the title, description and the section's content.
 *
 * This is the left-aligned sibling of ui-core's `StepHeader`, which centres
 * itself for the booking wizard. `ClaimsFormLayout` puts a left-aligned title
 * and rule above these sections, so a centred step badge would float away from
 * everything above it — hence the layout stays here rather than borrowing that
 * component. The circle is smaller than StepHeader's 48px: here it numbers a
 * step in a list rather than announcing the one step you are on, so it marks
 * the section without competing with its title.
 *
 * The section owns no outer spacing — the parent `Stack` sets the rhythm
 * between sections, so one section can't drift out of step with the others.
 */
export function ClaimsSubmitClaimSection(props: SubmitClaimSectionProps) {
  return (
    // shrinkable so a wide control inside a section can narrow rather than
    // push the page into a horizontal scroll.
    <Row gap={6} align="start" shrinkable>
      {/* Sits a little low on purpose so the circle centres against the title's
          cap height rather than its line box. */}
      <div className="relative top-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
        {props.index}
      </div>
      <Stack gap={4} align="start">
        <Stack gap={0} align="start">
          <Heading level={2}>{props.title}</Heading>
          <T size="base" color="secondary">
            {props.description}
          </T>
        </Stack>
        {props.content}
      </Stack>
    </Row>
  );
}
